import { useCallback, useEffect, useRef } from 'react';

// ============================================================================
// USE NEW ORDER SOUND HOOK - YENİ SİPARİŞ SESİ (Web Audio API)
// ============================================================================

/** Ses altyapısının o anki durumu - ayarlar ekranındaki sınama için */
export interface NewOrderSoundStatus {
  /** Tarayıcı Web Audio API'yi destekliyor mu */
  supported: boolean;
  /** AudioContext durumu: 'running' ses çıkabilir, 'suspended' engelli demektir */
  state: AudioContextState | 'unavailable';
  /** Ses çalmayı engelleyen bir durum varsa açıklaması */
  problem: string | null;
}

export interface UseNewOrderSoundReturn {
  /** Yeni sipariş sesini çal */
  playNewOrderSound: () => void;
  /** Yeni sipariş sesini durdur */
  stopNewOrderSound: () => void;
  /** Sesi tek seferlik çal (ayarlardaki sınama butonu için) */
  testNewOrderSound: () => Promise<NewOrderSoundStatus>;
  /** Ses altyapısının durumunu döndür */
  getSoundStatus: () => NewOrderSoundStatus;
}

/**
 * Yeni sipariş geldiğinde uyarı sesi çalan hook
 * Dikkat çekici çift bip pattern'i - restoran sipariş uyarısı
 *
 * ÖNEMLİ - sesin çalmama sorunu:
 * Tarayıcılar ve Electron, kullanıcı sayfayla etkileşime girmeden oluşturulan
 * AudioContext'i 'suspended' durumda başlatır. Bu durumda oscillator.start()
 * hata FIRLATMAZ, ses de çıkmaz — sorun bu yüzden sessizce fark edilmiyordu.
 * Çözüm iki parçalı: (1) context'i tek sefer oluşturup resume() etmek,
 * (2) ilk kullanıcı etkileşiminde context'i açmak (unlock).
 */
export const useNewOrderSound = (): UseNewOrderSoundReturn => {
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  // Çalmakta olan osilatörler: durdurulduğunda kuyrukta bekleyen bipler susmalı.
  const activeOscillatorsRef = useRef<OscillatorNode[]>([]);

  const getAudioContextClass = (): typeof AudioContext | null => {
    if (typeof window === 'undefined') return null;
    return window.AudioContext || (window as any).webkitAudioContext || null;
  };

  /**
   * Paylaşılan AudioContext'i döndürür, yoksa oluşturur.
   *
   * Her çalmada yeni context oluşturmak, kullanıcı etkileşimiyle açılmış
   * context'i çöpe atıp yeniden 'suspended' bir context yaratmak demekti.
   */
  const ensureAudioContext = useCallback((): AudioContext | null => {
    const AudioContextClass = getAudioContextClass();
    if (!AudioContextClass) return null;

    if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
      try {
        audioContextRef.current = new AudioContextClass();
      } catch (error) {
        console.error('📢 [NewOrderSound] AudioContext oluşturulamadı:', error);
        return null;
      }
    }

    return audioContextRef.current;
  }, []);

  const getSoundStatus = useCallback((): NewOrderSoundStatus => {
    if (!getAudioContextClass()) {
      return {
        supported: false,
        state: 'unavailable',
        problem: 'Bu tarayıcı ses çalmayı desteklemiyor.',
      };
    }

    const ctx = audioContextRef.current;
    if (!ctx || ctx.state === 'closed') {
      return { supported: true, state: ctx ? ctx.state : 'unavailable', problem: null };
    }

    return {
      supported: true,
      state: ctx.state,
      problem: ctx.state === 'suspended'
        ? 'Ses sistemi tarayıcı tarafından engellendi. Ekrana bir kez tıklayıp tekrar deneyin.'
        : null,
    };
  }, []);

  /**
   * Yeni sipariş sesini durdur
   *
   * AudioContext bilinçli olarak KAPATILMIYOR: bir kez açılan (resume edilmiş)
   * context kapatılırsa bir sonraki sipariş yine engelli context ile başlardı.
   */
  const stopNewOrderSound = useCallback(() => {
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    }

    activeOscillatorsRef.current.forEach(oscillator => {
      try {
        oscillator.stop();
        oscillator.disconnect();
      } catch (e) {
        // Zaten durmuş olabilir
      }
    });
    activeOscillatorsRef.current = [];
  }, []);

  /**
   * Dikkat çekici uyarı sesi - 3 kısa bip pattern'i
   * Yüksek-Düşük-Yüksek frekans ile aciliyet hissi
   */
  const playAlertPattern = useCallback((ctx: AudioContext) => {
    const now = ctx.currentTime;

    const beeps = [
      { freq: 1200, start: 0, duration: 0.12 },      // Yüksek bip
      { freq: 800, start: 0.15, duration: 0.12 },    // Düşük bip
      { freq: 1400, start: 0.30, duration: 0.15 },   // Daha yüksek bip
    ];

    beeps.forEach(({ freq, start, duration }) => {
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);

      oscillator.frequency.value = freq;
      oscillator.type = 'square'; // Keskin, dikkat çekici ses

      gainNode.gain.setValueAtTime(0, now + start);
      gainNode.gain.linearRampToValueAtTime(0.25, now + start + 0.02); // Hızlı yükseliş
      gainNode.gain.setValueAtTime(0.25, now + start + duration - 0.02);
      gainNode.gain.linearRampToValueAtTime(0, now + start + duration); // Hızlı düşüş

      oscillator.start(now + start);
      oscillator.stop(now + start + duration);

      activeOscillatorsRef.current.push(oscillator);
      oscillator.onended = () => {
        activeOscillatorsRef.current = activeOscillatorsRef.current.filter(o => o !== oscillator);
      };
    });
  }, []);

  /**
   * Yeni sipariş sesini çal (loop)
   */
  const playNewOrderSound = useCallback(() => {
    stopNewOrderSound();

    const ctx = ensureAudioContext();
    if (!ctx) {
      console.error('📢 [NewOrderSound] Ses altyapısı kullanılamıyor');
      return;
    }

    const start = () => {
      try {
        playAlertPattern(ctx);
        intervalIdRef.current = setInterval(() => playAlertPattern(ctx), 1500);
      } catch (error) {
        console.error('📢 [NewOrderSound] Ses çalınamadı:', error);
      }
    };

    // Engelli context'te oscillator.start() sessizce hiçbir şey yapmaz;
    // önce resume edilmeli.
    if (ctx.state === 'suspended') {
      ctx.resume()
        .then(start)
        .catch(error => {
          console.error('📢 [NewOrderSound] Ses sistemi açılamadı (tarayıcı engeli):', error);
        });
      return;
    }

    start();
  }, [stopNewOrderSound, ensureAudioContext, playAlertPattern]);

  /**
   * Ayarlar ekranındaki sınama butonu için: sesi bir kez çalar ve
   * ses sisteminin gerçekten çalışıp çalışmadığını raporlar.
   */
  const testNewOrderSound = useCallback(async (): Promise<NewOrderSoundStatus> => {
    const ctx = ensureAudioContext();
    if (!ctx) return getSoundStatus();

    if (ctx.state === 'suspended') {
      try {
        await ctx.resume();
      } catch (error) {
        console.error('📢 [NewOrderSound] Sınama: ses sistemi açılamadı:', error);
        return getSoundStatus();
      }
    }

    try {
      playAlertPattern(ctx);
    } catch (error) {
      console.error('📢 [NewOrderSound] Sınama: ses çalınamadı:', error);
    }

    return getSoundStatus();
  }, [ensureAudioContext, getSoundStatus, playAlertPattern]);

  /**
   * Ses kilidini aç: ilk kullanıcı etkileşiminde context'i resume et.
   *
   * Sipariş gece yarısı, kimse ekrana dokunmadan gelebilir. Ekran açıldıktan
   * sonraki ilk tıklama/tuş context'i kalıcı olarak açar; böylece sipariş
   * geldiğinde ses hazır olur.
   */
  useEffect(() => {
    const unlock = () => {
      const ctx = ensureAudioContext();
      if (ctx && ctx.state === 'suspended') {
        ctx.resume().catch(() => { /* sonraki etkileşimde tekrar denenecek */ });
      }
    };

    const events: Array<keyof DocumentEventMap> = ['pointerdown', 'keydown', 'touchstart'];
    events.forEach(event => document.addEventListener(event, unlock));

    // Sayfa yüklendiğinde de dene: Electron'da autoplayPolicy açıksa
    // etkileşim beklemeden çalışır.
    unlock();

    return () => {
      events.forEach(event => document.removeEventListener(event, unlock));
    };
  }, [ensureAudioContext]);

  // Ekran kapanırken context'i serbest bırak
  useEffect(() => {
    return () => {
      if (intervalIdRef.current) clearInterval(intervalIdRef.current);
      const ctx = audioContextRef.current;
      audioContextRef.current = null;
      if (ctx && ctx.state !== 'closed') {
        ctx.close().catch(() => { /* zaten kapanmış olabilir */ });
      }
    };
  }, []);

  return {
    playNewOrderSound,
    stopNewOrderSound,
    testNewOrderSound,
    getSoundStatus,
  };
};

export default useNewOrderSound;
